import { AbstractPreviewData, FilePreviewData } from './types';
import { MontaProduct } from '@domain/connection/models/MontaProduct';

export class DataTransformationService {
  /**
   * Transform Monta product data into abstract preview data format
   */
  static transformMontaDataToAbstract(
    products: MontaProduct[],
    syncType: 'all' | 'warehouse' | 'location' | null,
    selectedWarehouse?: { id: string; name: string } | null,
    selectedLocation?: { id: string; name: string } | null,
    warehouses?: { id: string; name: string }[],
    locations?: { id: string; name: string }[],
    selectedSheet?: string
  ): AbstractPreviewData {
    const getStockColumns = () => {
      if (syncType === 'all') {
        return [
          'StockInboundForecasted', 'StockQuarantaine', 'StockAll', 'StockBlocked', 
          'StockInTransit', 'StockReserved', 'StockInboundHistory', 'StockAvailable', 
          'StockWholeSaler', 'StockOpen', 'MaxPhysicalStock', 'MinPhysicalStock'
        ];
      } else if (syncType === 'warehouse') {
        return [
          'StockQuarantaine', 'StockAll', 'StockBlocked', 'StockInTransit', 
          'StockReserved', 'StockAvailable', 'StockOpen', 'MaxPhysicalStock', 'MinPhysicalStock'
        ];
      } else if (syncType === 'location') {
        return [
          'StockAll', 'StockQuarantaine', 'StockBlocked', 'StockInTransit', 
          'StockInPicking', 'MaxPhysicalStock', 'MinPhysicalStock'
        ];
      }
      return [];
    };

    const getStockData = (product: MontaProduct, selectedSheet?: string) => {
      if (syncType === 'all') {
        return product.Product.Stock;
      } else if (syncType === 'warehouse' && selectedSheet) {
        const warehouseName = selectedSheet.replace('Warehouse: ', '');
        const warehouse = warehouses?.find(w => w.name === warehouseName);
        if (warehouse) {
          return product.Product.Stock.PerWarehouse?.find(
            (w: any) => w.Warehouse?.Guid === warehouse.id
          );
        }
      } else if (syncType === 'location' && selectedSheet) {
        const locationName = selectedSheet.replace('Location: ', '');
        const location = locations?.find(l => l.name === locationName);
        if (location) {
          return product.Product.Stock.PerLocation?.find(
            (l: any) => l.LocationCode === location.id
          );
        }
      }
      return null;
    };

    const stockColumns = getStockColumns();
    
    // Get the maximum number of barcodes and product barcodes across all products
    const maxBarcodes = Math.max(...products.map(p => p.Product.Barcodes?.length || 0));
    const maxProductBarcodes = Math.max(...products.map(p => p.Product.ProductBarcodes?.length || 0));
    
    // Create barcode column headers with bracket notation
    const barcodeColumns = Array.from({ length: maxBarcodes }, (_, i) => `Barcodes[${i}]`);
    const productBarcodeColumns = Array.from({ length: maxProductBarcodes }, (_, i) => `ProductBarcodes[${i}]`);
    
    const headers = ['Product', 'SKU', ...barcodeColumns, ...productBarcodeColumns, ...stockColumns];
    
    const rows = products.map(product => {
      const stockData = getStockData(product, selectedSheet);
      
      // Split barcodes into individual columns
      const barcodeValues = Array.from({ length: maxBarcodes }, (_, i) => 
        product.Product.Barcodes?.[i] || ''
      );
      
      // Split product barcodes into individual columns
      const productBarcodeValues = Array.from({ length: maxProductBarcodes }, (_, i) => {
        const productBarcode = product.Product.ProductBarcodes?.[i];
        return productBarcode ? `${productBarcode.Barcode} (${productBarcode.Quantity})` : '';
      });
      
      const row = [
        product.Product.Description,
        product.Product.Sku,
        ...barcodeValues,
        ...productBarcodeValues,
        ...stockColumns.map(col => (stockData as any)?.[col]?.toString() || '0')
      ];
      
      return row;
    });

    // Generate sheet names based on sync type and available options
    let sheetNames: string[] = [];
    console.log('Generating sheet names:', { syncType, warehouses, locations });
    
    if (syncType === 'all') {
      sheetNames = ['All Stock'];
    } else if (syncType === 'warehouse') {
      if (warehouses && warehouses.length > 0) {
        sheetNames = warehouses.map(w => `Warehouse: ${w.name}`);
      } else {
        sheetNames = ['No Warehouses Available'];
      }
    } else if (syncType === 'location') {
      if (locations && locations.length > 0) {
        sheetNames = locations.map(l => `Location: ${l.name}`);
      } else {
        sheetNames = ['No Locations Available'];
      }
    }
    
    console.log('Generated sheet names:', sheetNames);

    return {
      headers,
      rows,
      columnMapping: {},
      firstRowIsHeader: true,
      headerRowIndex: 0,
      sheetNames,
      selectedSheet: sheetNames[0] || undefined
    };
  }

  /**
   * Transform file data into abstract preview data format
   */
  static transformFileDataToAbstract(
    fileData: FilePreviewData
  ): AbstractPreviewData {
    return {
      ...fileData,
    };
  }

  /**
   * Transform raw file data (headers and rows) into abstract preview data format
   */
  static transformRawFileDataToAbstract(
    headers: string[],
    rows: string[][],
    sheetNames?: string[],
    selectedSheet?: string
  ): AbstractPreviewData {
    return {
      headers,
      rows,
      sheetNames,
      selectedSheet,
      columnMapping: {},
      firstRowIsHeader: true,
      headerRowIndex: 0
    };
  }
} 